// SPDX-License-Identifier: AGPL-3.0-or-later

import {BOTTOM_PARKED_IDLE_MS, BOTTOM_SETTLE_MS} from '@app/features/read_state/utils/UnreadCatchUp';

export interface CatchUpTimerHandle {
	setTimeout(handler: () => void, ms: number): number;
	clearTimeout(id: number): void;
}

const browserTimers: CatchUpTimerHandle = {
	setTimeout: (handler, ms) => window.setTimeout(handler, ms),
	clearTimeout: (id) => {
		window.clearTimeout(id);
	},
};

/** Settle (100 ms) then parked-idle (300 ms) at the live bottom. Survives React callback identity. */
export class CatchUpBottomClocks {
	settledAtBottom = false;
	parkedIdleAtBottom = false;
	parkedBeforeGesture = false;
	private settleTimer: number | null = null;
	private parkedTimer: number | null = null;

	constructor(
		private readonly atLiveBottom: () => boolean,
		private readonly onSettled: () => void,
		private readonly timers: CatchUpTimerHandle = browserTimers,
	) {}

	reset(): void {
		this.settledAtBottom = false;
		this.parkedIdleAtBottom = false;
		this.parkedBeforeGesture = false;
		this.clearSettle();
		this.clearParked();
	}

	dispose(): void {
		this.reset();
	}

	noteUserGesture(): boolean {
		const atLiveBottom = this.atLiveBottom();
		if (!atLiveBottom && !this.settledAtBottom) {
			return false;
		}
		this.parkedBeforeGesture = this.parkedIdleAtBottom;
		this.scheduleParked();
		return true;
	}

	clearParkedBeforeGesture(): void {
		this.parkedBeforeGesture = false;
	}

	interruptParked(): void {
		this.parkedBeforeGesture = false;
		this.clearParked();
	}

	updateFromScroller(): void {
		if (!this.atLiveBottom()) {
			this.settledAtBottom = false;
			this.clearParked();
			this.clearSettle();
			return;
		}
		this.settledAtBottom = false;
		this.clearParked();
		this.clearSettle();
		this.settleTimer = this.timers.setTimeout(() => {
			this.settleTimer = null;
			if (!this.atLiveBottom()) {
				return;
			}
			this.settledAtBottom = true;
			this.scheduleParked();
			this.onSettled();
		}, BOTTOM_SETTLE_MS);
	}

	private scheduleParked(): void {
		this.parkedIdleAtBottom = false;
		this.clearParked();
		this.parkedTimer = this.timers.setTimeout(() => {
			this.parkedTimer = null;
			if (this.atLiveBottom() && this.settledAtBottom) {
				this.parkedIdleAtBottom = true;
			}
		}, BOTTOM_PARKED_IDLE_MS);
	}

	private clearSettle(): void {
		if (this.settleTimer != null) {
			this.timers.clearTimeout(this.settleTimer);
			this.settleTimer = null;
		}
	}

	private clearParked(): void {
		this.parkedIdleAtBottom = false;
		if (this.parkedTimer != null) {
			this.timers.clearTimeout(this.parkedTimer);
			this.parkedTimer = null;
		}
	}
}
